// src/services/userService.ts
import apiClient from "../api/apiClient";

const TENANT_ID = process.env.REACT_APP_TENANT_ID ?? "1";

// CONFIG: Point this to your backend's asset path
const ASSET_BASE = process.env.REACT_APP_ASSET_URL ?? "";

export interface UserSession {
    deviceId: string;
    deviceName: string;
    firstLogin: string;
    lastUsed: string;
    active: boolean;
}

export interface User {
    id: number;
    tenantId: string;
    email: string;
    firstName: string;
    lastName: string;
    displayName: string;
    role: string;
    district?: string | null;
    isSubscribed: boolean;
    registered: boolean;
    school?: string;
    medium?: string;
    className?: string;
    profilePhotoUrl?: string | null;
    mobile?: string;
    address?: string;
    classId?: number;
    notificationId?: string;
    sessions?: UserSession[];
}

export interface SubscriptionDTO {
    id: number;
    name: string;
    monthlyPrice: number;
    quarterlyPrice: number;
    halfYearlyPrice: number;
    yearlyPrice: number;
    purchasable: boolean;
    isSubscribed?: boolean | null;
    expiry?: string;
}

export interface UserDetailsResponse {
    profile: User;
    subscriptions: SubscriptionDTO[];
}

export interface GetUsersParams {
    page?: number;
    size?: number;
    role?: string;           // Filter by Role
    subscribed?: boolean;    // Filter by Subscription
    search?: string;
}

// Helper: Constructs the full image URL
export const getFullUrl = (path: string | undefined | null) => {
    if (!path) return "";
    if (path.startsWith("http")) return path;

    // Remove trailing slash from base
    const baseUrl = ASSET_BASE.replace(/\/+$/, "");
    // Ensure path has leading slash
    const cleanPath = path.startsWith("/") ? path : `/${path}`;

    return `${baseUrl}${cleanPath}`;
};

export async function getUsers(params: GetUsersParams = {}) {
    console.log("Fetching users with params:", params);
    // GET /api/admin/users
    const resp = await apiClient.get("/admin/users", {
        params: {
            ...params,
            tenantId: TENANT_ID,
        },
    });

    const data = resp.data;

    // SAFEGUARD: If backend returns a double-wrapped array [[...]], unwrap it.
    if (Array.isArray(data) && data.length === 1 && Array.isArray(data[0])) {
        console.warn("Detected nested array response, unwrapping...");
        return data[0];
    }

    return data;
}

export async function getUserDetails(userId: number): Promise<UserDetailsResponse> {
    const resp = await apiClient.get("/admin/users/details", {
        params: { userId, tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function deleteUser(userId: number): Promise<void> {
    await apiClient.delete(`/admin/users/${userId}`, {
        params: { tenantId: TENANT_ID }
    });
}

export interface CreateUserPayload {
    firstName: string;
    lastName?: string;
    email?: string;
    mobile: string;
    school?: string;
    medium?: string;
    className?: string;
    district?: string;
    address?: string;
    role?: string;
}

export async function createUser(payload: CreateUserPayload): Promise<User> {
    const resp = await apiClient.post("/admin/users", payload, {
        params: { tenantId: TENANT_ID }
    });
    return resp.data;
}

export async function checkUserExists(params: { mobile?: string; email?: string }): Promise<{ mobileExists: boolean; emailExists: boolean }> {
    const resp = await apiClient.get('/admin/users/check', {
        params: { ...params, tenantId: TENANT_ID }
    });
    return resp.data;
}